import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { Link } from 'react-router-dom';

import { type VisibilitySelectorValue } from '~/components/visibility/visibility-selector.utils';

import { useCaraAttachments } from '~/components/cara-input/use-cara-attachments';
import { useClipboardPaste } from '~/components/cara-input/use-clipboard-paste';
import { useFileDrop } from '~/components/cara-input/use-file-drop';
import { useDraftPersistence } from '~/components/cara-input/use-draft-persistence';
import { usePagedDocumentsEnabled } from '~/components/use-paged-documents-enabled';
import { useAttachmentValidation } from '~/components/cara-input/use-attachment-validation';
import {
  type CaraAttachment,
  type CaraTemplateAttachmentKind,
  idsByKind,
  pickerInitialSelectionFromAttachments,
  templateAttachment
} from '~/components/cara-input/cara-attachment';
import { type CopilotActionError, normalizeCopilotActionError } from '~/lib/copilot-action-error';
import { AmsDocumentPicker } from '~/components/ams-picker';

import { AttachmentPickerButton } from './cara-input-attachment-button';
import { CaraInputAttachments } from './cara-input-attachments';
import { CaraInputDropOverlay } from './cara-input-drop-overlay';
import { CaraInputErrors } from './cara-input-errors';
import {
  CaraInputTemplatePickerDialog,
  type TemplatePickerOption
} from './cara-input-template-picker-dialog';
import { CreateWorkspaceButton } from './cara-input-create-workspace-button';
import { type EffortPicker } from '~/components/presets/effort-level';
import { EffortLevelSelect } from '~/components/presets/effort-level-select';
import { TemplateLimitCount } from '~/components/presets/template-limit';

/** Picker prop shape — caller supplies `options`; selection lives inside `CaraInput`. */
export type CaraInputTemplatePicker = {
  options: TemplatePickerOption[];
  /** Show a spinner inside the dialog while options are still loading. */
  loading?: boolean;
  /** Overrides the default copy, which refers to the compose-box message. */
  description?: React.ReactNode;
};

/**
 * Composable Cara compose box.
 *
 * Every interactive concern lives in its own hook or subcomponent so this
 * file stays mostly layout:
 *
 * - `useDraftPersistence`     — localStorage-backed draft text (optional).
 * - `useCaraAttachments`      — single source of truth for queued chips
 *                                (files + library picks).
 * - `useFileDrop`             — drag-and-drop file detection.
 * - `useClipboardPaste`       — clipboard screenshot / file paste.
 * - `useFilePicker`           — owned inside `AttachmentPickerButton`.
 * - `useAttachmentValidation` — derives client-side errors (empty / oversize
 *                                files) and blocks submit.
 * - `CaraInputAttachments`        — chip row in the action-row's empty slot.
 * - `CaraInputDropOverlay`        — visual indicator while dragging.
 * - `CaraInputTemplatePickerDialog` — reusable multi-select dialog for the
 *                                three library pickers; emits selections
 *                                back into `useCaraAttachments.add(...)`.
 * - `CaraInputErrors`             — error list below the action row.
 */
export const CaraInput = (props: {
  onSubmit: (message: string, attachments: CaraAttachment[]) => Promise<void>;
  inline?: boolean;
  loading?: boolean;
  error?: CopilotActionError | string;
  /** Clear parent-held submit errors when the user adds or removes attachments. */
  onAttachmentsChanged?: () => void;
  onStop?: () => void;
  stopLoading?: boolean;
  /**
   * When set, the draft text is persisted to `localStorage` under this key
   * and restored on mount / tab focus. Pass `undefined` (the default) to
   * keep the textarea ephemeral — appropriate for the inline follow-up box
   * inside a live workspace.
   */
  draftStorageKey?: string;
  /**
   * Optional per-file ceiling for client-side validation. When omitted, the
   * only client-side check is "empty file".
   */
  maxFileSizeBytes?: number;
  /** Custom-form library picker — shown as a menu item + dialog. */
  customFormPicker?: CaraInputTemplatePicker;
  /** HTML document templates picker ("Templates" in Library). */
  htmlTemplatePicker?: CaraInputTemplatePicker;
  /** Word document templates picker ("Word Docs" in Library). */
  wordTemplatePicker?: CaraInputTemplatePicker;
  /** Excel spreadsheet templates picker ("Excel Templates" in Library). */
  excelTemplatePicker?: CaraInputTemplatePicker;
  /** Enables Smart Fill actions for an existing Cara workspace. */
  smartFillWorkspaceId?: string;
  /** Shows "Browse AMS" and opens `AmsDocumentPicker` from the menu. */
  enableAmsDocumentPicker?: boolean;
  /**
   * Optional visibility-at-creation control. When present, the create button
   * renders as a split action with the visibility selector in the trailing
   * segment.
   */
  visibilityPicker?: {
    value: VisibilitySelectorValue;
    onChange: (next: VisibilitySelectorValue) => void;
    disallowPrivate?: boolean;
  };
  /**
   * Optional effort-level control beside the send button. Only for a box that
   * starts a workspace: the level is fixed once the workspace exists.
   */
  effortPicker?: EffortPicker;
  /**
   * What sits in the effort picker's place in a box that continues a
   * workspace: its level, shown but not changeable.
   */
  effortBadge?: React.ReactNode;
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState<
    CaraTemplateAttachmentKind | 'ams-document' | null
  >(null);
  // Local "in-flight submit" flag. Flips true synchronously the moment the
  // user clicks send and stays true until `props.onSubmit` resolves. We OR
  // it with `props.loading` so the spinner appears immediately even when
  // the parent's loading flag is gated on an async subscription update.
  const [submitting, setSubmitting] = React.useState(false);
  // Fallback error storage for the case where `props.onSubmit` throws and
  // the caller doesn't set `props.error` itself. Parent-supplied errors
  // win in display priority (`props.error ?? submitError`).
  const [submitError, setSubmitError] = React.useState<CopilotActionError | string | undefined>();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const keepDraftsPerKey = usePagedDocumentsEnabled();
  const { query, setQuery, clearDraft } = useDraftPersistence(props.draftStorageKey ?? null, {
    keepDraftsPerKey
  });

  const handleAttachmentsChanged = React.useCallback(() => {
    // Match the legacy behavior of clearing the submit error as soon as
    // the user mutates the attachment queue — the previous failure is
    // probably no longer relevant once they've changed inputs.
    setSubmitError(undefined);
    props.onAttachmentsChanged?.();
  }, [props.onAttachmentsChanged]);

  const attachments = useCaraAttachments({ onChange: handleAttachmentsChanged });

  const loading = props.loading || submitting;
  const displayError = normalizeCopilotActionError(props.error ?? submitError);

  const drop = useFileDrop({
    onDrop: attachments.addFiles,
    disabled: loading
  });

  const handlePaste = useClipboardPaste({
    onPaste: attachments.addFiles,
    disabled: loading
  });

  const validation = useAttachmentValidation({
    attachments: attachments.attachments,
    maxFileSizeBytes: props.maxFileSizeBytes
  });

  const amsPickerInitialSelection = React.useMemo(
    () => pickerInitialSelectionFromAttachments(attachments.attachments),
    [attachments.attachments]
  );

  const resize = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    // Snap the textarea itself to its full content height so the browser
    // never auto-scrolls the textarea's content to keep the cursor visible
    // (which is what was clipping the top during the wrapper's animation).
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useLayoutEffect(() => {
    resize();
  }, [resize, query]);

  const canSubmit =
    !loading &&
    !validation.isInvalid &&
    (query.trim().length > 0 || attachments.attachments.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(undefined);
    try {
      await props.onSubmit(query.trim(), attachments.attachments);
      // `clearDraft` synchronously removes the persisted localStorage entry
      // (when one exists) so the draft doesn't survive a post-submit navigation
      // out of this component — the state-change-driven persistence effect
      // would otherwise race against unmount.
      clearDraft();
      attachments.clear();
      // Match the legacy compose UX: drop focus and snap to the bottom so
      // the freshly-sent message + Cara's response land in view. The short
      // delay lets React commit the new conversation row before we measure
      // scrollHeight. We resolve the nearest scrollable ancestor at call
      // time so this works both when the compose box lives inside an
      // internal scroll container (workspace) and when the page itself
      // scrolls (Copilot home).
      const startNode = textareaRef.current;
      textareaRef.current?.blur();
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          scrollToBottom(startNode);
        }, 200);
      }
    } catch (err) {
      // Keep draft text + attachments intact so the user can retry. Stash a
      // fallback message; the parent's `props.error` (if set) still wins.
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const submitOnEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Safari's IME-confirming Enter arrives after compositionend, flagged only by keyCode 229.
    const isComposingText = event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229;
    const isNewLineShortcut = event.shiftKey;
    if (event.key !== 'Enter' || isNewLineShortcut || isComposingText) return;
    event.preventDefault();
    void handleSubmit();
  };

  return (
    <div
      className={clsx(
        'relative bg-white dark:bg-neutral-800 p-5 rounded-3xl mt-8 box-content overflow-hidden flex flex-col gap-2',
        props.inline
          ? 'shadow-chat-inline transition-shadow duration-300'
          : 'shadow transition-shadow duration-300',
        isFocused && !props.inline && 'shadow-lg',
        isFocused && props.inline && 'shadow-chat-inline-lg'
      )}
      onDragEnter={drop.handlers.onDragEnter}
      onDragLeave={drop.handlers.onDragLeave}
      onDragOver={drop.handlers.onDragOver}
      onDrop={drop.handlers.onDrop}
    >
      <Headless.Textarea
        ref={textareaRef}
        rows={1}
        // bg-transparent because the forms plugin paints every textarea white,
        // which would sit as a white slab inside the dark composer.
        className="block w-full p-0 m-0 bg-transparent font-body text-base border-0 resize-none outline-none ring-0 focus:outline-0 focus:ring-0 focus:border-0 overflow-hidden"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={submitOnEnter}
        enterKeyHint="send"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onInput={resize}
        onPaste={handlePaste}
        placeholder="Ask Cara..."
      />
      <div className="flex flex-row gap-2 justify-between">
        <div className="flex min-w-0 flex-grow flex-row flex-wrap items-center gap-2">
          <CaraInputAttachments
            attachments={attachments.attachments}
            errors={validation.errors}
            onRemove={attachments.remove}
          />
        </div>
        <div className="flex flex-row items-center gap-2 justify-end self-end">
          <AttachmentPickerButton
            disabled={loading}
            onAttachFiles={attachments.addFiles}
            onBrowseAms={
              props.enableAmsDocumentPicker ? () => setOpenDialog('ams-document') : undefined
            }
            onPickCustomForm={
              props.customFormPicker ? () => setOpenDialog('custom-form') : undefined
            }
            onPickWordTemplate={
              props.wordTemplatePicker ? () => setOpenDialog('word-template') : undefined
            }
            onPickExcelTemplate={
              props.excelTemplatePicker ? () => setOpenDialog('excel-template') : undefined
            }
            onPickHtmlTemplate={
              props.htmlTemplatePicker ? () => setOpenDialog('html-template') : undefined
            }
            smartFillWorkspaceId={props.smartFillWorkspaceId}
          />
          {props.effortPicker && (
            <EffortLevelSelect
              variant="pill"
              value={props.effortPicker.value}
              onChange={props.effortPicker.onChange}
              choices={props.effortPicker.choices}
              disabled={loading}
            />
          )}
          {!props.effortPicker && props.effortBadge}
          <CreateWorkspaceButton
            loading={loading}
            stopLoading={props.stopLoading}
            onStop={props.onStop}
            onSubmit={handleSubmit}
            disabled={!canSubmit}
            visibilityPicker={props.visibilityPicker}
          />
        </div>
      </div>
      <CaraInputErrors attachmentErrors={validation.errors} submitError={displayError} />
      <CaraInputDropOverlay visible={drop.isDragging} />
      {props.enableAmsDocumentPicker && openDialog === 'ams-document' && (
        <AmsDocumentPicker
          open
          onClose={() => setOpenDialog(null)}
          initialSelectedFiles={amsPickerInitialSelection.initialSelectedFiles}
          initialContextPins={amsPickerInitialSelection.initialContextPins}
          onConfirm={(result) => attachments.replaceFromPickerConfirm(result)}
        />
      )}
      <TemplatePickerDialogs
        attachments={attachments.attachments}
        add={attachments.add}
        remove={attachments.remove}
        openDialog={openDialog === 'ams-document' ? null : openDialog}
        onCloseDialog={() => setOpenDialog(null)}
        customFormPicker={props.customFormPicker}
        wordTemplatePicker={props.wordTemplatePicker}
        excelTemplatePicker={props.excelTemplatePicker}
        htmlTemplatePicker={props.htmlTemplatePicker}
      />
    </div>
  );
};

/**
 * Walk up from `el` looking for the nearest vertically scrollable ancestor
 * and scroll it to the bottom. Falls back to scrolling the window when no
 * intermediate scroll container exists — i.e. when the page itself scrolls.
 */
const scrollToBottom = (el: HTMLElement | null) => {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight
    ) {
      node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
      return;
    }
    node = node.parentElement;
  }
  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
};

/**
 * Renders all three picker dialogs and translates dialog-emitted id lists
 * into `useCaraAttachments` add/remove calls. Extracted from the main
 * component so the JSX up there stays focused on layout.
 *
 * Only one dialog is `open` at a time (controlled by `openDialog`), so
 * mounting all three unconditionally is cheap and avoids subtle remount
 * issues when switching from one picker to another.
 */
export const TemplatePickerDialogs = (props: {
  attachments: CaraAttachment[];
  add: (...next: CaraAttachment[]) => void;
  remove: (id: string) => void;
  openDialog: CaraTemplateAttachmentKind | null;
  onCloseDialog: () => void;
  customFormPicker?: CaraInputTemplatePicker;
  wordTemplatePicker?: CaraInputTemplatePicker;
  excelTemplatePicker?: CaraInputTemplatePicker;
  htmlTemplatePicker?: CaraInputTemplatePicker;
}) => {
  const syncSelection = (
    kind: CaraTemplateAttachmentKind,
    options: TemplatePickerOption[],
    nextIds: string[]
  ) => {
    const prevIds = idsByKind(props.attachments, kind);

    // Only drop ids the combobox could represent: it silently omits selections
    // absent from `options`, so an unrepresentable pin would look deselected.
    for (const id of prevIds) {
      if (!nextIds.includes(id) && options.some((o) => o.id === id)) props.remove(id);
    }

    // Add new selections, looking up titles from the options the parent gave us.
    const additions: CaraAttachment[] = [];
    for (const id of nextIds) {
      if (prevIds.includes(id)) continue;
      const opt = options.find((o) => o.id === id);
      if (opt) additions.push(templateAttachment(kind, opt));
    }
    if (additions.length > 0) props.add(...additions);
  };

  return (
    <>
      {props.customFormPicker && (
        <CaraInputTemplatePickerDialog
          open={props.openDialog === 'custom-form'}
          onClose={props.onCloseDialog}
          title="Fill custom form"
          description={
            props.customFormPicker.description ?? (
              <>
                Select up to <TemplateLimitCount /> forms from your{' '}
                <Link
                  to="/app/library/forms"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  form library
                </Link>
                . Cara will fill them using your message and attachments.
              </>
            )
          }
          fieldLabel="Forms"
          emptyText="No ready forms in your library."
          placeholder="Select forms"
          loading={props.customFormPicker.loading}
          loadingMessage={'Loading forms\u2026'}
          options={props.customFormPicker.options}
          selectedIds={idsByKind(props.attachments, 'custom-form')}
          onChange={(ids) => syncSelection('custom-form', props.customFormPicker!.options, ids)}
        />
      )}

      {props.wordTemplatePicker && (
        <CaraInputTemplatePickerDialog
          open={props.openDialog === 'word-template'}
          onClose={props.onCloseDialog}
          title="Select Word docs"
          description={
            props.wordTemplatePicker.description ?? (
              <>
                Select Word document templates from your{' '}
                <Link
                  to="/app/library/word-templates"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Word docs library
                </Link>
                . Cara will fill them using your message and attachments.
              </>
            )
          }
          fieldLabel="Word Docs"
          emptyText="No Word templates in your library."
          placeholder="Select Word docs"
          options={props.wordTemplatePicker.options}
          selectedIds={idsByKind(props.attachments, 'word-template')}
          onChange={(ids) => syncSelection('word-template', props.wordTemplatePicker!.options, ids)}
        />
      )}

      {props.excelTemplatePicker && (
        <CaraInputTemplatePickerDialog
          open={props.openDialog === 'excel-template'}
          onClose={props.onCloseDialog}
          title="Select Excel templates"
          description={
            <>
              Select Excel spreadsheet templates from your{' '}
              <Link
                to="/app/library/excel-templates"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Excel templates library
              </Link>
              . Cara will fill them using your message and attachments.
            </>
          }
          fieldLabel="Excel Templates"
          emptyText="No Excel templates in your library."
          placeholder="Select Excel templates"
          options={props.excelTemplatePicker.options}
          selectedIds={idsByKind(props.attachments, 'excel-template')}
          onChange={(ids) =>
            syncSelection('excel-template', props.excelTemplatePicker!.options, ids)
          }
        />
      )}

      {props.htmlTemplatePicker && (
        <CaraInputTemplatePickerDialog
          open={props.openDialog === 'html-template'}
          onClose={props.onCloseDialog}
          title="Select templates"
          description={
            props.htmlTemplatePicker.description ?? (
              <>
                Select document templates from your{' '}
                <Link
                  to="/app/library/templates"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  template library
                </Link>
                . Cara will use them to generate styled documents.
              </>
            )
          }
          fieldLabel="Templates"
          emptyText="No templates in your library."
          placeholder="Select templates"
          options={props.htmlTemplatePicker.options}
          selectedIds={idsByKind(props.attachments, 'html-template')}
          onChange={(ids) => syncSelection('html-template', props.htmlTemplatePicker!.options, ids)}
        />
      )}
    </>
  );
};
